export interface CustomAppFrameOptions {
	src: string;
	runtimeOrigin: string;
	title: string;
	/** True when the app declares `device_location:use`. */
	allowsDeviceLocation: boolean;
}

/**
 * Creates the browser boundary for untrusted Custom App output.
 *
 * The runtime document and all executable assets are served through a trusted Custom App runtime URL. The iframe retains
 * its own per-app origin. Modal permission enables printing; downloads still require the trusted host bridge. The
 * app may show itself in fullscreen, for example in a popup on a second display. Geolocation is delegated only to an
 * app that declares it; the runtime's Permissions-Policy header enforces the same, but Firefox ignores that header.
 */
export function createCustomAppFrame(options: CustomAppFrameOptions): HTMLIFrameElement {
	if (options.title.trim() === "") throw new Error("Custom App frame title must not be blank.");
	const src = new URL(options.src);
	const runtimeOrigin = parseRuntimeOrigin(options.runtimeOrigin);
	validateSecureHttpUrl(src, "Custom App frame URL");
	if (runtimeOrigin.origin === window.location.origin) {
		throw new Error("Custom App runtime origin must differ from the Aavo frontend origin.");
	}
	if (src.origin !== runtimeOrigin.origin) {
		throw new Error(`Custom App frame URL must use the trusted runtime origin '${runtimeOrigin.origin}'.`);
	}

	const frame = document.createElement("iframe");
	frame.src = src.href;
	frame.title = options.title;
	frame.loading = "eager";
	frame.referrerPolicy = "no-referrer";
	frame.setAttribute("sandbox", "allow-scripts allow-same-origin allow-modals");
	frame.setAttribute("allow", options.allowsDeviceLocation ? "fullscreen; geolocation" : "fullscreen");
	return frame;
}

function parseRuntimeOrigin(value: string): URL {
	const runtimeOrigin = new URL(value);
	validateSecureHttpUrl(runtimeOrigin, "Custom App runtime origin");
	if (runtimeOrigin.pathname !== "/" || runtimeOrigin.search !== "" || runtimeOrigin.hash !== "") {
		throw new Error("Custom App runtime origin must not contain a path, query, or fragment.");
	}
	return runtimeOrigin;
}

function validateSecureHttpUrl(url: URL, name: string): void {
	if (url.username !== "" || url.password !== "") {
		throw new Error(`${name} cannot contain credentials.`);
	}
	if (url.protocol !== "https:" && (url.protocol !== "http:" || !isLoopbackHostname(url.hostname))) {
		throw new Error(`${name} requires HTTPS outside local development.`);
	}
}

function isLoopbackHostname(hostname: string): boolean {
	return (
		hostname === "localhost" || hostname.endsWith(".localhost") || hostname === "127.0.0.1" || hostname === "[::1]"
	);
}
